import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { C, font } from "../theme";
import { Card } from "./Card";
import { Label } from "./Label";
import { Button } from "./Button";
import { FieldInput } from "./Field";
import { ConfirmDialog } from "./ConfirmDialog";
import type { CategoryDto, CreateCategoryRequest } from "../types";

interface CategoryManagerProps {
  categories: CategoryDto[];
  submitting: boolean;
  submitError: string | null;
  onCreate: (payload: CreateCategoryRequest) => void;
  onUpdate: (categoryId: string, payload: CreateCategoryRequest) => void;
  onDelete: (categoryId: string) => void;
  onClose: () => void;
}

type View = "list" | "create" | "edit";

export function CategoryManager({ categories, submitting, submitError, onCreate, onUpdate, onDelete, onClose }: CategoryManagerProps) {
  const [view, setView] = useState<View>("list");
  const [editing, setEditing] = useState<CategoryDto | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CategoryDto | null>(null);
  const [pendingNav, setPendingNav] = useState(false);

  const goToList = () => {
    setView("list");
    setEditing(null);
    setName("");
    setError(null);
  };

  const startCreate = () => {
    setView("create");
    setEditing(null);
    setName("");
    setError(null);
  };

  const startEdit = (category: CategoryDto) => {
    setView("edit");
    setEditing(category);
    setName(category.name);
    setError(null);
  };

  // When a submit settles, decide where to land: success returns to the
  // list (so the user sees the result), failure stays on the form with the error.
  useEffect(() => {
    if (!pendingNav) return;
    if (submitting) return; // still in flight
    setPendingNav(false);
    if (submitError === null) {
      goToList();
    }
  }, [pendingNav, submitting, submitError]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed === "") {
      setError("Informe o nome");
      return;
    }
    if (trimmed.length > 80) {
      setError("Nome deve ter no máximo 80 caracteres");
      return;
    }
    setError(null);
    setPendingNav(true);
    if (view === "edit" && editing) {
      onUpdate(editing.id, { name: trimmed });
    } else {
      onCreate({ name: trimmed });
    }
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontFamily: font,
    fontSize: 12,
    fontWeight: 600,
    color: C.gray,
    marginBottom: 4,
    marginTop: 12,
  };

  const errorStyle: React.CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,17,17,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      <Card style={{ width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }}>
        {view === "list" ? (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Label style={{ color: C.ink, fontSize: 13, letterSpacing: 0.5 }}>Gerenciar categorias</Label>
              <Button variant="ghost" onClick={onClose} ariaLabel="Fechar" style={{ fontSize: 16, lineHeight: 1 }}>
                ✕
              </Button>
            </div>

            {submitError && (
              <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>
                {submitError}
              </div>
            )}

            {categories.length === 0 ? (
              <div
                style={{
                  marginTop: 18,
                  padding: "26px 12px",
                  textAlign: "center",
                  border: `1px dashed ${C.faint}`,
                  borderRadius: 12,
                  color: C.light,
                  fontSize: 13,
                }}
              >
                Nenhuma categoria cadastrada ainda.
              </div>
            ) : (
              <div style={{ marginTop: 14, borderTop: `1px solid ${C.faint}` }}>
                {categories.map(c => (
                  <div
                    key={c.id}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.faint}`, fontSize: 13 }}
                  >
                    <span style={{ fontWeight: 600, color: C.ink }}>{c.name}</span>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <Button
                        variant="ghost"
                        onClick={() => startEdit(c)}
                        ariaLabel={`Editar ${c.name}`}
                        title="Editar"
                        style={{ textDecoration: "none", padding: 6, display: "inline-flex", alignItems: "center" }}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setPendingDelete(c)}
                        ariaLabel={`Deletar ${c.name}`}
                        title="Deletar"
                        style={{ textDecoration: "none", padding: 6, display: "inline-flex", alignItems: "center", color: C.red }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              variant="outline"
              onClick={startCreate}
              style={{ width: "100%", marginTop: 16, borderStyle: "dashed", borderColor: C.gray, color: C.ink }}
            >
              + Nova categoria
            </Button>
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Button
                variant="ghost"
                onClick={goToList}
                disabled={submitting}
                ariaLabel="Voltar"
                title="Voltar"
                style={{ textDecoration: "none", padding: 0, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: C.gray, lineHeight: 1 }}
              >
                <ArrowLeft size={14} /> Voltar
              </Button>
              <div style={{ flex: 1 }} />
              <Label style={{ color: C.ink, fontSize: 13, letterSpacing: 0.5, margin: 0 }}>
                {view === "edit" ? "Editar categoria" : "Nova categoria"}
              </Label>
              <div style={{ flex: 1 }} />
              <Button variant="ghost" onClick={onClose} ariaLabel="Fechar" style={{ fontSize: 16, lineHeight: 1 }}>
                ✕
              </Button>
            </div>

            <form onSubmit={handleSubmit} noValidate style={{ marginTop: 6 }}>
              <label htmlFor="category-name" style={labelStyle}>Nome *</label>
              <FieldInput id="category-name" value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Bebidas" maxLength={80} />
              {error && <div style={errorStyle}>{error}</div>}
              {submitError && (
                <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>
                  {submitError}
                </div>
              )}
              <Button type="submit" disabled={submitting} style={{ width: "100%", marginTop: 18 }}>
                {submitting ? "Salvando..." : view === "edit" ? "Salvar alterações" : "Criar categoria"}
              </Button>
            </form>
          </>
        )}
      </Card>

      {pendingDelete && (
        <ConfirmDialog
          title="Excluir categoria"
          confirmLabel="Excluir"
          onConfirm={() => {
            onDelete(pendingDelete.id);
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        >
          Tem certeza que deseja excluir <b style={{ color: C.ink }}>"{pendingDelete.name}"</b>? Essa ação não pode ser desfeita.
        </ConfirmDialog>
      )}
    </div>
  );
}
